import { Button, useTranslation } from '@hbs-us/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useMemo, useState } from 'react';

import { useAccess } from '../../auth/useAccess.js';
import { ConfirmDialog } from '../_money/ConfirmDialog.js';
import { useMoneyFormatters } from '../_money/useMoneyFormatters.js';
import { EmployeesEmptyState } from './components/EmployeesEmptyState.js';
import { EmployeesTable } from './components/EmployeesTable.js';
import { StatusFilterPills } from './components/StatusFilterPills.js';
import type { EmployeeListItemDto, EmployeesResponse } from './contractsMirror.js';
import type { EmployeesSearch } from './searchParams.js';
import { useArchiveEmployeeMutation } from './useEmployeeQuery.js';

/**
 * The redesigned employees directory (UI-SPEC archetype A — list + detail) on the
 * locked Velocity brand. Page wrapper + header (title, subtitle, single primary
 * "+ Add Employee" CTA) + a labelled search input + the active/archived status-filter
 * pill row (server counts) + the semantic employees table — or the empty state.
 *
 * The coarse endpoint (22-05) owns the active/archived split + the counts; this view
 * renders the returned payload and applies the free-text `?q` filter IN-MEMORY over
 * the already-loaded directory (small dataset, no extra round-trip). The deactivate /
 * restore action routes through the focus-trapped `ConfirmDialog` (Shared 7 — never a
 * native confirm). Pay fields are preserved on the detail/form (D-03), not on the list.
 */

export interface EmployeesViewProps {
  data: EmployeesResponse;
  search: EmployeesSearch;
}

/** Case-insensitive match of the search text against name / email / phone. */
function matchesQuery(employee: EmployeeListItemDto, q: string): boolean {
  const needle = q.toLowerCase();
  return (
    employee.name.toLowerCase().includes(needle) ||
    (employee.email ?? '').toLowerCase().includes(needle) ||
    (employee.phone ?? '').toLowerCase().includes(needle)
  );
}

export function EmployeesView({ data, search }: EmployeesViewProps) {
  const { t } = useTranslation();
  const fmt = useMoneyFormatters();
  const navigate = useNavigate();
  // The page is open to anyone holding employees.view; each write answers to its own key.
  const canAccess = useAccess();
  const canCreate = canAccess({ permission: 'employees.create' });
  const canEdit = canAccess({ permission: 'employees.edit' });
  const canArchive = canAccess({ permission: 'employees.archive' });

  const employees = data.employees ?? [];
  const statusCounts = data.statusCounts ?? {};
  const showArchived = Boolean(search.show_archived);

  // Controlled search input; the URL `?q` is the source of truth (deep-linkable).
  const [queryText, setQueryText] = useState(search.q ?? '');

  const filtered = useMemo(() => {
    const q = queryText.trim();
    if (!q) return employees;
    return employees.filter((employee) => matchesQuery(employee, q));
  }, [employees, queryText]);

  // The archive/restore confirm target (Shared 7 ConfirmDialog).
  const [confirmTarget, setConfirmTarget] = useState<EmployeeListItemDto | null>(null);
  const archiveMutation = useArchiveEmployeeMutation();

  const hasData = filtered.length > 0;
  const searching = queryText.trim().length > 0;

  function goToDetail(employee: EmployeeListItemDto) {
    void navigate({ to: '/employees/$id', params: { id: String(employee.id) } });
  }
  function goToEdit(employee: EmployeeListItemDto) {
    void navigate({ to: '/employees/$id/edit', params: { id: String(employee.id) } });
  }

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-semibold text-3xl text-foreground">
            {showArchived ? t('employees:archivedTitle') : t('employees:title')}
          </h1>
          <p className="mt-1 max-w-2xl text-muted-foreground text-sm">
            {showArchived ? t('employees:archivedSubtitle') : t('employees:subtitle')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {showArchived ? (
            <Button asChild variant="secondary">
              <Link to="/employees" search={search.q ? { q: search.q } : {}}>
                {t('employees:actions.backToActive')}
              </Link>
            </Button>
          ) : canCreate ? (
            <Button asChild>
              <Link to="/employees/new">{t('employees:actions.addEmployee')}</Link>
            </Button>
          ) : null}
        </div>
      </header>

      <div className="mb-4">
        <label htmlFor="employee-search" className="sr-only">
          {t('employees:search.label')}
        </label>
        <div className="relative max-w-md">
          <Search
            className="pointer-events-none absolute top-3.5 left-3 size-4 text-muted-foreground"
            aria-hidden="true"
          />
          <input
            id="employee-search"
            type="search"
            value={queryText}
            onChange={(event) => setQueryText(event.target.value)}
            placeholder={t('employees:search.placeholder')}
            className="h-11 w-full rounded-md border border-border bg-card pr-3 pl-9 text-foreground text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          />
        </div>
      </div>

      <StatusFilterPills search={search} statusCounts={statusCounts} />

      {hasData ? (
        <EmployeesTable
          employees={filtered}
          fmt={fmt}
          onView={goToDetail}
          onEdit={canEdit ? goToEdit : undefined}
          onDeactivate={canArchive ? setConfirmTarget : undefined}
        />
      ) : (
        <EmployeesEmptyState showArchived={showArchived} searching={searching} />
      )}

      <ConfirmDialog
        open={confirmTarget !== null}
        heading={
          confirmTarget?.archivedAt
            ? t('employees:deactivateDialog.restoreHeading')
            : t('employees:deactivateDialog.heading')
        }
        body={
          confirmTarget?.archivedAt
            ? t('employees:deactivateDialog.restoreBody')
            : t('employees:deactivateDialog.body')
        }
        confirmLabel={
          confirmTarget?.archivedAt
            ? t('employees:deactivateDialog.restoreConfirm')
            : t('employees:deactivateDialog.confirm')
        }
        cancelLabel={t('employees:deactivateDialog.cancel')}
        confirmPending={archiveMutation.isPending}
        onCancel={() => setConfirmTarget(null)}
        onConfirm={() => {
          // Archive/restore the employee via the api-host write endpoint (Plan 22-24), then
          // invalidate so the directory re-syncs to the authoritative server state.
          const target = confirmTarget;
          if (!target) return;
          archiveMutation.mutate(
            { employeeId: target.id, restore: Boolean(target.archivedAt) },
            { onSuccess: () => setConfirmTarget(null) },
          );
        }}
      />
    </div>
  );
}
